import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  MessageSquare,
  Wrench,
  FileText,
  Bell,
  Plus,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import Layout from '../components/Layout';
import { StatCard } from '../components/StatCard';
import { QuickActionCard } from '../components/QuickActionCard';
import { NotificationItem } from '../components/NotificationItem';
import { RecentActivityItem } from '../components/RecentActivityItem';
import { getApiUrl } from '../config/api';
import authFetch from '../utils/authFetch';

// Statuses considered "open" for a service request.
const OPEN_SERVICE_STATUSES = ['pending', 'acknowledged', 'scheduled'];

// Format an ISO timestamp as a short relative string (e.g. "2 hours ago").
function timeAgo(dateString) {
  if (!dateString) return '';
  const then = new Date(dateString);
  if (isNaN(then.getTime())) return '';
  const seconds = Math.floor((Date.now() - then.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const intervals = [
    { label: 'year', secs: 31536000 },
    { label: 'month', secs: 2592000 },
    { label: 'day', secs: 86400 },
    { label: 'hour', secs: 3600 },
    { label: 'minute', secs: 60 },
  ];
  for (const { label, secs } of intervals) {
    const count = Math.floor(seconds / secs);
    if (count >= 1) return `${count} ${label}${count > 1 ? 's' : ''} ago`;
  }
  return 'Just now';
}

function truncate(text, max = 80) {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}\u2026` : text;
}

// A message is inbound (from the tenant) when it was not sent to the tenant's phone.
function isInbound(msg) {
  return msg.message_to !== msg.tenant_phone;
}

// Map a service request status to the icon status the activity item understands.
function serviceActivityStatus(status) {
  switch (status) {
    case 'completed':
      return 'completed';
    case 'scheduled':
      return 'scheduled';
    case 'cancelled':
      return 'default';
    default:
      return 'pending';
  }
}

function serviceActivityAction(status) {
  switch (status) {
    case 'completed':
      return 'Service request completed';
    case 'scheduled':
      return 'Service request scheduled';
    case 'acknowledged':
      return 'Service request acknowledged';
    case 'cancelled':
      return 'Service request cancelled';
    default:
      return 'New service request';
  }
}

// Pick the most relevant timestamp for a service request, used for sorting/display.
function serviceRequestTimestamp(sr) {
  return (
    sr.service_schedule_date ||
    sr.acknowledged_date ||
    sr.date_requested ||
    null
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    properties: 0,
    tenants: 0,
    serviceRequests: 0,
    unreadMessages: 0
  });
  const [notifications, setNotifications] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchJson = async (path) => {
      try {
        const res = await authFetch(getApiUrl(path));
        if (!res.ok) return null;
        return await res.json();
      } catch (err) {
        console.error(`Error fetching ${path}:`, err);
        return null;
      }
    };

    const loadDashboard = async () => {
      const [propsData, tenantsData, serviceData, unreadData, messagesData] =
        await Promise.all([
          fetchJson('/api/v1/property/list'),
          fetchJson('/api/v1/tenant/list'),
          fetchJson('/api/v1/service/list'),
          fetchJson('/api/v1/message/unread-count'),
          fetchJson('/api/v1/message/list'),
        ]);

      if (cancelled) return;

      const properties = propsData?.properties || [];
      const tenants = tenantsData?.tenants || [];
      const serviceRequests = serviceData?.service_requests || [];
      const messages = messagesData?.messages || [];

      // Lookup for resolving property names on service-request activity items.
      const propertyById = new Map(
        properties.map((p) => [p.id, p.name || p.address])
      );

      const activeTenants = tenants.filter(
        (t) => t.status === 'active' && t.property_id
      ).length;
      const openServiceRequests = serviceRequests.filter((sr) =>
        OPEN_SERVICE_STATUSES.includes(sr.request_status)
      ).length;

      setStats({
        properties: properties.length,
        tenants: activeTenants,
        serviceRequests: openServiceRequests,
        unreadMessages: unreadData?.unread_count || 0,
      });

      // Tenant notifications: the last 2 unread inbound messages.
      const unreadInbound = messages
        .filter((m) => isInbound(m) && m.message_read === false)
        .sort((a, b) => new Date(b.date_added) - new Date(a.date_added));

      setNotifications(
        unreadInbound.slice(0, 2).map((m) => ({
          id: m.id,
          tenant: m.tenant_name || 'Unknown',
          property: m.property_address || '',
          message: m.message_body || '',
          time: timeAgo(m.date_added),
          type: 'message',
          unread: true,
        }))
      );

      // Recent activity: merged, time-sorted feed of service requests and
      // incoming tenant messages.
      const serviceActivity = serviceRequests.map((sr) => {
        const ts = serviceRequestTimestamp(sr);
        const propertyName = propertyById.get(sr.property_id);
        const summary = truncate(sr.tenant_description || sr.landlord_description || 'Service request');
        return {
          id: `sr-${sr.id}`,
          action: serviceActivityAction(sr.request_status),
          description: propertyName ? `${summary} \u2014 ${propertyName}` : summary,
          status: serviceActivityStatus(sr.request_status),
          _ts: ts ? new Date(ts).getTime() : 0,
          time: timeAgo(ts),
        };
      });

      const messageActivity = messages
        .filter(isInbound)
        .map((m) => ({
          id: `msg-${m.id}`,
          action: 'New tenant message',
          description: `${m.tenant_name || 'Unknown'}: ${truncate(m.message_body, 60)}`,
          status: 'message',
          _ts: m.date_added ? new Date(m.date_added).getTime() : 0,
          time: timeAgo(m.date_added),
        }));

      const merged = [...serviceActivity, ...messageActivity]
        .sort((a, b) => b._ts - a._ts)
        .slice(0, 5);

      setRecentActivity(merged);
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, []);

  // Stats cards data
  const statsData = [
    { label: 'Properties', value: String(stats.properties), icon: Home, color: 'text-emerald-600' },
    { label: 'Active Tenants', value: String(stats.tenants), icon: MessageSquare, color: 'text-blue-500' },
    { label: 'Open Service Requests', value: String(stats.serviceRequests), icon: Wrench, color: 'text-amber-500' },
    { label: 'Unread Messages', value: String(stats.unreadMessages), icon: Bell, color: 'text-red-500' },
  ];

  return (
    <Layout
      pageTitle={`Welcome back, ${localStorage.getItem('first_name') || ''}!`}
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsData.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <QuickActionCard
                icon={Plus}
                title="Add Property"
                description="Register a new rental property"
                color="bg-[#16A34A]"
                onClick={() => navigate('/properties', { state: { openAddModal: true } })}
              />
              <QuickActionCard
                icon={Wrench}
                title="Add Appliance"
                description="Track appliances at properties"
                color="bg-[#3498DB]"
                onClick={() => navigate('/maintenance')}
              />
              <QuickActionCard
                icon={FileText}
                title="Create Service Request"
                description="Log a new maintenance issue"
                color="bg-[#F59E0B]"
                onClick={() => navigate('/service')}
              />
              <QuickActionCard
                icon={Sparkles}
                title="AI Diagnostics"
                description="Get help diagnosing issues"
                color="bg-[#8B5CF6]"
                onClick={() => navigate('/maintenance')}
              />
              <QuickActionCard
                icon={MessageSquare}
                title="Message Tenant"
                description="Send SMS to your tenants"
                color="bg-[#06B6D4]"
                onClick={() => navigate('/messages')}
              />
              <QuickActionCard
                icon={Home}
                title="View All Properties"
                description="Manage your rental portfolio"
                color="bg-[#64748B]"
                onClick={() => navigate('/properties')}
              />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
            <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Recent Activity</h3>
            {recentActivity.length === 0 ? (
              <p className="text-[#7F8C8D] text-center py-8">No recent activity to display.</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((activity) => (
                  <RecentActivityItem key={activity.id} {...activity} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notifications from Tenants */}
        <div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#2C3E50]">Tenant Notifications</h3>
              <span className="bg-[#E74C3C] text-white text-xs font-semibold px-2 py-1 rounded-full">
                {stats.unreadMessages}
              </span>
            </div>
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="mx-auto text-[#16A34A] mb-2" size={48} />
                <p className="text-[#7F8C8D]">All caught up!</p>
                <p className="text-sm text-[#7F8C8D]">No new notifications</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <NotificationItem key={notification.id} {...notification} />
                ))}
                <button
                  onClick={() => navigate('/messages')}
                  className="w-full text-center text-[#16A34A] hover:text-[#27AE60] font-medium py-2 text-sm transition-colors"
                >
                  View All Notifications
                </button>
              </div>
            )}
          </div>

          {/* Tips Card */}
          <div className="bg-gradient-to-br from-[#16A34A] to-[#27AE60] rounded-lg shadow-sm p-6 mt-6 text-white">
            <div className="flex items-start gap-3">
              <Sparkles size={24} className="flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-2">Pro Tip</h4>
                <p className="text-sm text-white/90">
                  Use AI Diagnostics to quickly identify appliance issues before scheduling a service visit.
                  Save time and money!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
